import { fetchApi } from './api';
import type {
    SCFilterOptions,
    SurchargeInvoice,
    InvoiceLine,
    ExistingAllocation,
    AllocationPayload,
    SurchargeProduct,
    PriceOverviewRow,
    PriceOverviewSummary,
    SalesOverviewPeriod,
    SalesOverviewSummary,
    SCComment,
} from '../types/salesControlling';

const BASE = '/sales-controlling';

export const salesControllingApi = {
    // Filters
    async getFilters(): Promise<SCFilterOptions> {
        return fetchApi(`${BASE}/filters`);
    },

    // Surcharge Invoices
    async getSurchargeInvoices(params: {
        company?: string;
        dateFrom?: string;
        dateTo?: string;
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<{ data: SurchargeInvoice[]; pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } }> {
        const sp = new URLSearchParams();
        if (params.company) sp.set('company', params.company);
        if (params.dateFrom) sp.set('dateFrom', params.dateFrom);
        if (params.dateTo) sp.set('dateTo', params.dateTo);
        if (params.status) sp.set('status', params.status);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));

        const res = await fetch(`/api/v1${BASE}/surcharge-invoices?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (res.status === 401) {
            const { redirectToLogin } = await import('../utils/auth-redirect');
            redirectToLogin();
            throw new Error('Session expired');
        }
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'API error');
        return { data: body.data, pagination: body.pagination };
    },

    // Invoice Details
    async getInvoiceDetails(documentCode: string, company: string): Promise<{
        lines: InvoiceLine[];
        existingAllocations: ExistingAllocation[];
    }> {
        return fetchApi(`${BASE}/surcharge-invoices/${encodeURIComponent(documentCode)}?company=${encodeURIComponent(company)}`);
    },

    // Allocations
    async saveAllocation(payload: AllocationPayload): Promise<{ saved: number }> {
        return fetchApi(`${BASE}/surcharge-allocations`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async deleteAllocation(documentCode: string, company: string): Promise<void> {
        return fetchApi(`${BASE}/surcharge-allocations/${encodeURIComponent(documentCode)}?company=${encodeURIComponent(company)}`, {
            method: 'DELETE',
        });
    },

    // Surcharge Products
    async getSurchargeProducts(): Promise<SurchargeProduct[]> {
        return fetchApi(`${BASE}/surcharge-products`);
    },

    async saveSurchargeProduct(product: Partial<SurchargeProduct> & {
        productID: string;
        productCode: string;
        surchargeName: string;
        surchargeType: string;
        companyCode: string;
    }): Promise<{ id: number }> {
        return fetchApi(`${BASE}/surcharge-products`, {
            method: 'POST',
            body: JSON.stringify(product),
        });
    },

    async deleteSurchargeProduct(id: number): Promise<void> {
        return fetchApi(`${BASE}/surcharge-products/${id}`, {
            method: 'DELETE',
        });
    },

    // Price Overview
    async getPriceOverview(params: {
        company?: string;
        dateFrom?: string;
        dateTo?: string;
        customerID?: string;
        productID?: string;
        salesManagerID?: string;
        lowMarginOnly?: boolean;
        marginThreshold?: number;
        deviationThreshold?: number;
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        supplierSearch?: string;
        supplierID?: string;
    }): Promise<{ data: { rows: PriceOverviewRow[]; summary: PriceOverviewSummary; availableFilters: { salesManagers: Array<{ id: string; name: string }>; suppliers: Array<{ id: string; name: string }>; customers: Array<{ id: string; name: string }>; products: Array<{ id: string; name: string }> } }; pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } }> {
        const sp = new URLSearchParams();
        if (params.company) sp.set('company', params.company);
        if (params.dateFrom) sp.set('dateFrom', params.dateFrom);
        if (params.dateTo) sp.set('dateTo', params.dateTo);
        if (params.customerID) sp.set('customerID', params.customerID);
        if (params.productID) sp.set('productID', params.productID);
        if (params.salesManagerID) sp.set('salesManagerID', params.salesManagerID);
        if (params.lowMarginOnly) sp.set('lowMarginOnly', 'true');
        if (params.marginThreshold != null) sp.set('marginThreshold', String(params.marginThreshold));
        if (params.deviationThreshold != null) sp.set('deviationThreshold', String(params.deviationThreshold));
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.supplierSearch) sp.set('supplierSearch', params.supplierSearch);
        if (params.supplierID) sp.set('supplierID', params.supplierID);

        const res = await fetch(`/api/v1${BASE}/price-overview?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (res.status === 401) {
            const { redirectToLogin } = await import('../utils/auth-redirect');
            redirectToLogin();
            throw new Error('Session expired');
        }
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'API error');
        return { data: body.data, pagination: body.pagination };
    },

    // Sales Overview
    async getSalesOverview(params: {
        company?: string;
        year?: number;
        businessUnit?: string;
        salesManagerID?: string;
        granularity?: string;
    }): Promise<{
        periods: SalesOverviewPeriod[];
        summary: SalesOverviewSummary;
    }> {
        const sp = new URLSearchParams();
        if (params.company) sp.set('company', params.company);
        if (params.year) sp.set('year', String(params.year));
        if (params.businessUnit) sp.set('businessUnit', params.businessUnit);
        if (params.salesManagerID) sp.set('salesManagerID', params.salesManagerID);
        if (params.granularity) sp.set('granularity', params.granularity);
        return fetchApi(`${BASE}/sales-overview?${sp}`);
    },

    // Comments
    async getComments(company: string): Promise<SCComment[]> {
        return fetchApi(`${BASE}/comments?company=${encodeURIComponent(company)}`);
    },

    async saveComment(data: {
        id?: number;
        companyCode: string;
        documentCode: string;
        documentLineNr: number;
        commentText: string;
    }): Promise<{ id: number }> {
        return fetchApi(`${BASE}/comments`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async deleteComment(id: number): Promise<void> {
        return fetchApi(`${BASE}/comments/${id}`, { method: 'DELETE' });
    },
};
